/**
 * Time on Page Tracking
 *
 * Tracks engagement by measuring time spent on each page.
 * Fires events at key intervals: 10s, 30s, 60s, 120s
 * Only counts "active" time (when tab is visible)
 */

const INTERVALS = [10, 30, 60, 120]; // seconds

export class TimeOnPageTracker {
  constructor(analytics) {
    this.analytics = analytics;
    this.startTime = null;
    this.trackedIntervals = new Set();
    this.timers = [];
    this.isVisible = true;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  /**
   * Start tracking time on current page
   */
  start() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Reset for new page
    this.reset();

    // Record start time
    this.startTime = Date.now();

    // Set up timers for each interval
    this.scheduleIntervals();

    // Listen for visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Schedule timers for each time interval
   */
  scheduleIntervals() {
    for (const seconds of INTERVALS) {
      const timer = setTimeout(() => {
        this.trackInterval(seconds);
      }, seconds * 1000);

      this.timers.push(timer);
    }
  }

  /**
   * Handle page visibility changes (tab switching)
   */
  handleVisibilityChange() {
    if (typeof document === 'undefined') {
      return;
    }

    const wasVisible = this.isVisible;
    this.isVisible = !document.hidden;

    // If user left tab, pause timers
    if (wasVisible && !this.isVisible) {
      this.pauseTimers();
    }

    // If user returned to tab, resume timers
    if (!wasVisible && this.isVisible) {
      this.resumeTimers();
    }
  }

  /**
   * Pause all active timers
   */
  pauseTimers() {
    // Clear existing timers
    this.clearTimers();

    // Calculate time already spent
    this.pausedTime = Date.now();
  }

  /**
   * Resume timers after pause
   */
  resumeTimers() {
    if (!this.pausedTime) {
      return;
    }

    const pauseDuration = Date.now() - this.pausedTime;
    const totalTimeSpent = (Date.now() - this.startTime - pauseDuration) / 1000;

    // Reschedule remaining intervals
    for (const seconds of INTERVALS) {
      if (!this.trackedIntervals.has(seconds)) {
        const remaining = seconds - totalTimeSpent;
        if (remaining > 0) {
          const timer = setTimeout(() => {
            this.trackInterval(seconds);
          }, remaining * 1000);

          this.timers.push(timer);
        }
      }
    }

    this.pausedTime = null;
  }

  /**
   * Track a time interval milestone
   */
  trackInterval(seconds) {
    // Skip if tab is not visible
    if (!this.isVisible) {
      return;
    }

    // Mark as tracked
    this.trackedIntervals.add(seconds);

    // Send analytics event
    if (this.analytics) {
      this.analytics.trackEvent('engagement.time_on_page', {
        duration: seconds,
        page: typeof window !== 'undefined' ? window.location.pathname : '',
      });
    }
  }

  /**
   * Stop tracking and clear all timers
   */
  stop() {
    this.clearTimers();

    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
  }

  /**
   * Clear all scheduled timers
   */
  clearTimers() {
    for (const timer of this.timers) {
      clearTimeout(timer);
    }
    this.timers = [];
  }

  /**
   * Reset tracking for new page
   */
  reset() {
    this.stop();
    this.trackedIntervals.clear();
    this.startTime = null;
    this.pausedTime = null;
    this.isVisible = true;
  }
}
